import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil, tap } from 'rxjs/operators';
import { PerListEntry } from '../../shared/per-list-entry';
import { PersonResponcibilityList } from '../../shared/person';
import { ClientApiService } from '../../services/client-api.service';
import { environment } from '../../../environments/environment';

/**
 * Reusable person/contact-picker dropdown, optionally filtered by a parent company.
 *
 * Key collaborator: {@link ClientApiService} — `getPerList()` / `getPerListByCompany(id)`.
 *
 * **Data-flow:** `parentCompany` change → `_reloadComponentData()` → list loaded + enriched
 * with `PERSRESPONSIBLESTR` → `_selectPerson()` resolves pre-selection or `firstAvailable`
 * default.
 *
 * `firstAvailable` flag logic: falls back to first entry with `PERSRESPONSIBLE >= 0`, then
 * to index `[0]` if no match.
 *
 * **Inputs summary:**
 * | Field              | Purpose                                                            |
 * |--------------------|--------------------------------------------------------------------|
 * | `parentCompany`    | Filters person list to this company's contacts; reloads on change  |
 * | `selectedID`       | Pre-selects the matching contact after list loads                  |
 * | `disabled`         | Passes through to the PrimeNG dropdown                            |
 * | `readonly`         | Prevents selection change without hiding the dropdown              |
 * | `showLabel`        | Toggles the label above the dropdown                              |
 * | `showEditButton`   | Reserved: edit-contact button (not currently rendered in template) |
 * | `showAddButton`    | Reserved: add-contact button (not currently rendered in template)  |
 * | `showSearchButton` | Reserved: advanced-search button (not currently rendered)          |
 * | `showClear`        | Shows the PrimeNG clear (×) icon                                   |
 * | `labelKey`         | Text content of the label                                          |
 * | `placeholder`      | Dropdown placeholder text                                          |
 * | `firstAvailable`   | Auto-selects first eligible contact when no `selectedID` is set    |
 *
 * **Output:** `selectedPersonID` — emits the selected contact's `PERSID` (debounced 100 ms)
 * or `null` on clear.
 */
@Component({
  selector: 'app-personselector',
  templateUrl: './personselector.component.html',
  styleUrls: ['./personselector.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class PersonselectorComponent implements OnInit, OnChanges, OnDestroy {

  _personslistMC: PerListEntry[] = [];
  _personMC: PerListEntry = null;

  _loading: boolean = false;

  /** Text content of the label displayed above the dropdown. */
  @Input() labelKey = 'Контактное лицо';

  /** Dropdown placeholder text. */
  @Input() placeholder = 'Выбрать контакт...';

  /** Filters the person list to this company's contacts; list reloads on every change. */
  @Input() parentCompany: number = null;

  /** Pre-selects the matching contact after the list loads. */
  @Input() selectedID: number = null;

  /** Passes through to the PrimeNG dropdown `disabled` property. */
  @Input() disabled: boolean = false;

  /** Prevents selection change without hiding the dropdown. */
  @Input() readonly: boolean = false;

  /** Toggles the label above the dropdown. */
  @Input() showLabel: boolean = true;

  /** Reserved: edit-contact button (not currently rendered in template). */
  @Input() showEditButton: boolean = true;

  /** Reserved: add-contact button (not currently rendered in template). */
  @Input() showAddButton: boolean = true;

  /** Reserved: advanced-search button (not currently rendered in template). */
  @Input() showSearchButton: boolean = true;

  /** Passes through to PrimeNG `showClear` — shows the clear (×) icon. */
  @Input() showClear: boolean = true;

  /** When `true` and no `selectedID` is set, auto-selects the first eligible contact. */
  @Input() firstAvailable: boolean = false;

  /** Emits the selected contact's `PERSID` (debounced 100 ms) or `null` on clear. */
  @Output() selectedPersonID: EventEmitter<number> = new EventEmitter();

  private readonly destroy$ = new Subject<void>();
  private readonly _reloadCancel$ = new Subject<void>();
  private readonly selectedPersonEmitterSubs$ = new Subject<number>();

  constructor(
    private cliApi: ClientApiService,
  ) { }

  /**
   * Loads the full or company-filtered person list, enriches each entry with
   * `PERSRESPONSIBLESTR` via {@link PersonResponcibilityList}, then calls
   * `_selectPerson()` to resolve any pre-selection. Cancels any prior in-flight
   * request before issuing a new one. Resets `_loading` on success or error.
   */
  private _reloadComponentData(): void {
    this._reloadCancel$.next();
    this._loading = true;
    (this.parentCompany
      ? this.cliApi.getPerListByCompany(this.parentCompany)
      : this.cliApi.getPerList()
    ).pipe(
      map(data => {
        this._personslistMC = data;
        this._personslistMC.forEach(e => {
          e.PERSRESPONSIBLESTR = PersonResponcibilityList.find(i => i.index === (e.PERSRESPONSIBLE || 0))?.value ?? '';
          e.disAllowSelect = !((e.PERSRESPONSIBLE || 0) >= 0);
        });
        this._selectPerson();
        this._loading = false;
      }),
      takeUntil(this._reloadCancel$),
      takeUntil(this.destroy$)
    ).subscribe({
      error: err => {
        console.error('*** PersonselectorComponent _reloadComponentData error:', err);
        this._loading = false;
      }
    });
  }

  /**
   * Syncs `_personMC` from the loaded list using `selectedID`. Applies the
   * `firstAvailable` fallback (first entry with `PERSRESPONSIBLE >= 0`, then index `[0]`).
   * Debounced-emits via `selectedPersonEmitterSubs$` if the resolved ID differs from the
   * current `selectedID`.
   */
  private _selectPerson(): void {
    this._personMC = null;
    if (this._personslistMC && (this._personslistMC.length > 0)) {
      if (this.selectedID) {
        this._personMC = this._personslistMC.find(x => x.PERSID === this.selectedID) ?? null;
      } else if (this.firstAvailable) {
        this._personMC = this._personslistMC.find(x => (x.PERSRESPONSIBLE || 0) >= 0);
        if (!this._personMC) {
          this._personMC = this._personslistMC[0];
        }
      }
    }

    if (this._personMC && (this.selectedID !== this._personMC.PERSID)) {
      this.selectedID = this._personMC.PERSID;
      this.selectedPersonEmitterSubs$.next(this.selectedID);
    }
  }

  /**
   * Reacts to `@Input()` changes: `parentCompany` triggers a full list reload;
   * `selectedID` re-syncs the dropdown model without a server round-trip.
   */
  ngOnChanges(changes: SimpleChanges): void {
    for (const propName of Object.keys(changes)) {
      const change = changes[propName];

      switch (propName) {
        case 'parentCompany':
          this._reloadComponentData();
          break;

        case 'selectedID':
          this._selectPerson();
          break;
      }
    }
  }

  /**
   * Completes `destroy$` to cancel all active subscriptions (list load and emitter pipeline).
   */
  ngOnDestroy(): void {
    console.log('*** PersonselectorComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Sets up the debounced `selectedPersonEmitterSubs$` → `selectedPersonID` emit pipeline,
   * then triggers the initial person list load.
   */
  ngOnInit(): void {
    this.selectedPersonEmitterSubs$.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      tap(id => {
        environment.production || console.log('*** PersonselectorComponent selectedPersonEmitterSubs$: ' + id);
        this.selectedPersonID.emit(id);
      }),
      takeUntil(this.destroy$)
    ).subscribe({
      error: err => console.error('*** PersonselectorComponent selectedPersonEmitterSubs$ error:', err)
    });

    this._reloadComponentData();
  }

  /**
   * Emits the selected contact's `PERSID` via the debounce subject on selection, or
   * emits `null` when the dropdown value is cleared programmatically.
   */
  onChangePS(_e: unknown): void {
    environment.production || console.log('*** PersonselectorComponent onChangePS: ' + JSON.stringify(this._personMC));
    if (this._personMC) {
      this.selectedID = this._personMC.PERSID;
      this.selectedPersonEmitterSubs$.next(this.selectedID);
    } else {
      this.selectedID = null;
      this.selectedPersonEmitterSubs$.next(null);
    }
  }

  /**
   * Resets `selectedID` and `_personMC` and emits `null` when the user clicks the
   * PrimeNG clear (×) button.
   */
  onClearPS(_e: unknown): void {
    console.log('*** PersonselectorComponent onClearPS: ' + JSON.stringify(_e));
    this.selectedID = null;
    this._personMC = null;
    this.selectedPersonEmitterSubs$.next(null);
  }
}
